import { useForm, Controller } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select, type SelectOption } from "@/components/ui/Select";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";
import type { CaseNote } from "./CaseNotesList";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CaseNoteFormValues {
  title: string;
  noteText: string;
  noteType: string;
  noteDate: string;
  noteTime: string;
}

interface CaseNoteEditorProps {
  personId: string;
  note?: CaseNote | null;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const noteTypeOptions: SelectOption[] = [
  { value: "general", label: "General" },
  { value: "clinical", label: "Clinical" },
  { value: "legal", label: "Legal" },
  { value: "case_management", label: "Case Management" },
  { value: "counseling", label: "Counseling" },
  { value: "advocacy", label: "Advocacy" },
  { value: "other", label: "Other" },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CaseNoteEditor({ personId, note, onClose }: CaseNoteEditorProps) {
  const queryClient = useQueryClient();
  const isEditing = !!note;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<CaseNoteFormValues>({
    defaultValues: {
      title: note?.title ?? "",
      noteText: note?.note_text ?? "",
      noteType: note?.note_type ?? "general",
      noteDate:
        note?.note_date ?? new Date().toISOString().split("T")[0],
      noteTime: note?.note_time ?? "",
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: CaseNoteFormValues) =>
      api.post(`/clients/${personId}/notes`, {
        title: data.title || undefined,
        noteText: data.noteText || undefined,
        noteType: data.noteType,
        noteDate: data.noteDate || undefined,
        noteTime: data.noteTime || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["case-notes", personId] });
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: CaseNoteFormValues) =>
      api.put(`/clients/${personId}/notes/${note!.id}`, {
        title: data.title || undefined,
        noteText: data.noteText || undefined,
        noteType: data.noteType,
        noteDate: data.noteDate || undefined,
        noteTime: data.noteTime || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["case-notes", personId] });
      onClose();
    },
  });

  const mutation = isEditing ? updateMutation : createMutation;

  function onSubmit(data: CaseNoteFormValues) {
    mutation.mutate(data);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onClose}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-lg font-semibold">
          {isEditing ? "Edit Case Note" : "New Case Note"}
        </h2>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Note Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Lock/Review status for existing notes */}
            {isEditing && note && (
              <div className="flex gap-4 rounded-md bg-muted/50 p-3 text-sm text-muted-foreground">
                {note.is_locked === 1 && (
                  <span className="text-amber-600">This note is locked</span>
                )}
                {note.reviewed_by && (
                  <span className="text-green-600">
                    Reviewed by{" "}
                    {note.reviewer_first_name && note.reviewer_last_name
                      ? `${note.reviewer_first_name} ${note.reviewer_last_name}`
                      : "supervisor"}
                    {note.reviewed_at &&
                      ` on ${new Date(note.reviewed_at).toLocaleDateString()}`}
                  </span>
                )}
              </div>
            )}

            <Input
              label="Title"
              {...register("title")}
              error={errors.title?.message}
              placeholder="Note title (optional)"
            />

            <div className="space-y-1.5">
              <label className="text-sm font-medium leading-none text-foreground">
                Note Content
              </label>
              <textarea
                {...register("noteText")}
                rows={8}
                placeholder="Enter note content..."
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              />
              {errors.noteText && (
                <p className="text-xs text-destructive">
                  {errors.noteText.message}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Controller
                name="noteType"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Note Type"
                    options={noteTypeOptions}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    required
                  />
                )}
              />

              <Input
                label="Date"
                type="date"
                {...register("noteDate")}
                error={errors.noteDate?.message}
              />

              <Input
                label="Time"
                type="time"
                {...register("noteTime")}
                error={errors.noteTime?.message}
              />
            </div>

            {/* Error message */}
            {mutation.isError && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {(mutation.error as Error)?.message ?? "An error occurred"}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button type="submit" loading={mutation.isPending}>
                {isEditing ? "Save Changes" : "Create Note"}
              </Button>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
